import media from 'common-ui-components/src/vtj/theme/media'
import { LanguageCode, toLanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { when } from 'mobx'
import { observer } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  ButtonProps,
  Heading,
  IconArrowLeft,
  IconArrowRight,
  InlineAlert,
  LoadingSpinner,
  Paragraph,
  RouterLink,
  Text,
  WizardNavigation,
  WizardNavigationItem,
  WizardNavigationItemProps,
} from 'suomifi-ui-components'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { certificateTypeToTranslationKeyMap } from 'todistukset-common/src/vtj/certificate.util'
import { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'
import { userBlockedReason } from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockedAnnouncement.util'
import BlockedAnnouncements from 'todistukset-ui/src/vtj/components/blocked-announcement/BlockedAnnouncements.react'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  StepValidatorParamsParam,
  getComponent,
  getCurrentStepItem,
  getDefaultRedirectPath,
  getIsGloballyDisabled,
  getNavigationItems,
  getNextStepPath,
  getPrevStepPath,
  getStepCount,
  getStepIndex,
  handleLanding,
  validateLanding,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizard.util'
import {
  ButtonsContainer,
  ContentContainer,
  ContentGridItem,
  ControlsContainer,
  NotificationContainer,
  OrderWizardPageLayout,
  SidebarGridItem,
  TitleGridItem,
  ValidationErrorList,
  WizardNavigationContainer,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardPage.sc'
import { type FirstErrorImperativeHandle } from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { getPropertyTranslationKey } from 'todistukset-ui/src/vtj/components/order-wizard/validation'
import { PageConfigComponentWithStepsProps } from 'todistukset-ui/src/vtj/components/page-router/TodistuksetRouter.types'
import {
  queueDialogRejectAetTodistuspyynto,
  queueDialogTranslationFormWithPreviousAddresses,
} from 'todistukset-ui/src/vtj/stores/actions/dialog-actions'
import { setShowValidationErrors } from 'todistukset-ui/src/vtj/stores/actions/steps-actions'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { shouldShowTranslationFormWithPreviousAddressesWarning } from 'todistukset-ui/src/vtj/stores/util/form.util'
import StepRoutes from 'todistukset-ui/src/vtj/todistukset-step-routes.enum'
import {
  getAetTodistuspyyntoIdFromPathname,
  useAetTodistuspyyntoId,
} from 'todistukset-ui/src/vtj/components/order-wizard-aet-signer/aetSignerSteps'
import ErrorPageType from 'common/src/vtj/ErrorPageType.enum'
import { assertUnreachableCode } from 'common/src/vtj/typescript.util'
import { isValueInEnum } from 'todistukset-common/src/vtj/type-guards'
import OrderFlowName, {
  orderFlowNameToCertificateType,
} from 'todistukset-ui/src/vtj/OrderFlowName.enum'
import ErrorPage from 'todistukset-ui/src/vtj/components/error-page/ErrorPage.react'
import { doesOrderExist } from 'todistukset-ui/src/vtj/components/order-wizard/OrderCompleteStep.util'
import {
  OnNextClick,
  Step,
  StepButtons,
  StepComponent,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import PageRoutes from 'todistukset-ui/src/vtj/todistukset-page-routes.enum'
import { LoadingSpinnerContainer } from 'todistukset-ui/src/vtj/components/price-list/PriceList.sc'

const showTranslationFormWithPreviousAddressesDialogWhenNecessary = ({
  store,
  certificateType,
}: {
  store: TodistuksetUi.Store
  certificateType: CertificateTypes
}) => {
  const shouldShowDialog = () => {
    if (!store.forms) {
      return false
    }
    const form = store.forms[certificateType]
    const { previousAddressDialogDismissed } = store.common
    return (
      !previousAddressDialogDismissed &&
      shouldShowTranslationFormWithPreviousAddressesWarning({
        certificateType,
        form,
      })
    )
  }
  return when(
    () => shouldShowDialog(),
    queueDialogTranslationFormWithPreviousAddresses
  )
}

const usePreviousValue = <T,>(value: T): T | undefined => {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

const OrderWizardPage = ({
  step,
  page,
  getSteps,
}: PageConfigComponentWithStepsProps) => {
  if (!isValueInEnum(page, OrderFlowName)) {
    throw new Error('Type check failed for "page"')
  }

  const store = useStore()
  const { lang, loggedInUser } = store.common
  const user = loggedInUser.get()
  const { t } = useTranslation()

  const orderFlowName = page
  const certificateType = orderFlowNameToCertificateType(orderFlowName)
  const stepIndexTitleRef = useRef<HTMLHeadingElement>(null)
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const previousStep = usePreviousValue(step)
  const steps = getSteps({ store, pathname })
  const { paymentId, aetTodistuspyyntoId } = useParams()

  const { loadTodistuspyynto, isLoading, error, aetTodistuspyynto } =
    store.aetSignerFormFn(Number(aetTodistuspyyntoId))

  useEffect(() => {
    if (page === PageRoutes.AETTODISTUS_SIGNER && aetTodistuspyyntoId) {
      if (!aetTodistuspyynto && !isLoading && !error) {
        loadTodistuspyynto(Number(aetTodistuspyyntoId))
      }
    }
  }, [
    loadTodistuspyynto,
    aetTodistuspyynto,
    isLoading,
    error,
    aetTodistuspyyntoId,
    page,
  ])

  useEffect(() => {
    if (!step) {
      return
    }
    handleLanding({
      orderFlowName,
      steps,
      step,
    })

    if (previousStep && previousStep !== step) {
      stepIndexTitleRef?.current?.focus()
    }
  }, [orderFlowName, steps, step, user, previousStep, stepIndexTitleRef])

  useEffect(() => {
    const disposer =
      showTranslationFormWithPreviousAddressesDialogWhenNecessary({
        store,
        certificateType,
      })
    return () => disposer()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isAllowed = validateLanding({
    stepValidatorParams: useStepValidationParams(
      certificateType,
      orderFlowName
    ),
    orderFlowName,
    steps,
    step,
  })
  useEffect(() => {
    if (!isAllowed) {
      const redirectPath = getDefaultRedirectPath({
        steps,
        lang: toLanguageCode(lang),
      })
      if (redirectPath !== pathname) {
        navigate(redirectPath, { replace: true })
      }
    }
  }, [isAllowed, steps, lang, navigate, pathname])

  if (
    step &&
    [StepRoutes.ORDER_COMPLETE, StepRoutes.UAET_ORDER_COMPLETE].includes(
      step
    ) &&
    !doesOrderExist({ store, certificateType, paymentId })
  ) {
    return <ErrorPage errors={[ErrorPageType.ORDER_ID_NOT_FOUND]} lang={lang} />
  }

  if (
    page === PageRoutes.AETTODISTUS_SIGNER &&
    (!aetTodistuspyynto || isLoading)
  ) {
    return (
      <LoadingSpinnerContainer>
        <LoadingSpinner
          status={error ? 'failed' : 'loading'}
          text={t('aetLoadingOrder')}
          textAlign="bottom"
          variant="small"
        />
      </LoadingSpinnerContainer>
    )
  }
  if (error) {
    return <ErrorPage errors={[ErrorPageType.PAGE_NOT_FOUND]} lang={lang} />
  }

  if (!isAllowed || step === undefined) {
    return <></>
  }

  return (
    <OrderWizardPageLayout>
      <Title certificateType={certificateType} orderFlowName={orderFlowName} />
      {!userBlockedReason(user, certificateType, orderFlowName) && (
        <>
          <Sidebar {...{ certificateType, orderFlowName, steps, step, lang }} />
          <Content
            {...{ certificateType, orderFlowName, steps, step, lang }}
            innerRef={stepIndexTitleRef}
          />
        </>
      )}
    </OrderWizardPageLayout>
  )
}

export default observer(OrderWizardPage)

const AnnouncementWrapper = observer(
  ({
    certificateType,
    orderFlowName,
  }: {
    certificateType: CertificateTypes
    orderFlowName: OrderFlowName
  }) => {
    const {
      common: { user, lang },
    } = useStore()
    return (
      <NotificationContainer>
        <BlockedAnnouncements
          certificateType={certificateType}
          orderFlowName={orderFlowName}
        />
        {userBlockedReason(user, certificateType, orderFlowName) && (
          <Paragraph>
            <RouterLink asComponent={Link} to={`/${lang}#top`}>
              <LocalizedString stringKey="orderWizardReturnToFrontPageLinkText" />
            </RouterLink>
          </Paragraph>
        )}
      </NotificationContainer>
    )
  }
)

const Title = ({
  certificateType,
  orderFlowName,
}: {
  certificateType: CertificateTypes
  orderFlowName: OrderFlowName
}) => {
  return (
    <TitleGridItem>
      <Heading variant="h1" data-test-id="todistus-title">
        <LocalizedString
          stringKey={certificateTypeToTranslationKeyMap[certificateType]}
        />
      </Heading>
      <AnnouncementWrapper
        certificateType={certificateType}
        orderFlowName={orderFlowName}
      />
    </TitleGridItem>
  )
}

interface SidebarProps {
  lang: LanguageCode
  certificateType: CertificateTypes
  orderFlowName: OrderFlowName
  steps: Step[]
  step: StepRoutes
}

const Sidebar = observer(
  ({ lang, certificateType, orderFlowName, steps, step }: SidebarProps) => {
    const navigationItems = getNavigationItems({
      stepValidatorParams: useStepValidationParams(
        certificateType,
        orderFlowName
      ),
      orderFlowName,
      steps,
      lang,
    })
    const isGloballyDisabled = getIsGloballyDisabled(step)

    const currentIndex = getStepIndex({ steps, step })
    const currentStep = getCurrentStepItem({
      stepValidatorParams: useStepValidationParams(
        certificateType,
        orderFlowName
      ),
      orderFlowName,
      steps,
      step,
      lang,
    })
    const allowStepNavigation = currentStep.buttons !== StepButtons.NONE
    const isOrderCompleteStep = [
      StepRoutes.ORDER_COMPLETE,
      StepRoutes.AET_ORDER_COMPLETE,
      StepRoutes.AET_SIGNER_ORDER_COMPLETE,
      StepRoutes.UAET_ORDER_COMPLETE,
    ].includes(step)

    const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false)
    const isMobile = useMediaQuery({
      maxWidth: media.tabletMaxWidth,
    })
    useEffect(() => {
      // Done after initial render to avoid ssr rehydration issues, the server always renders the desktop variant
      setIsSmallScreen(isMobile)
    }, [isMobile])
    const { t } = useTranslation()

    return (
      <SidebarGridItem>
        <WizardNavigationContainer>
          <WizardNavigation
            variant={isSmallScreen ? 'smallScreen' : 'default'}
            heading={t('wizardNavigationSteps')}
            aria-label={t('wizardNavigationSteps')}
            initiallyExpanded={false}
          >
            {navigationItems.map((item, index) => {
              const { navigationItemStatus, routerLinkProps } =
                getNavigationItemStatus({
                  isGloballyDisabled,
                  isOrderCompleteStep,
                  stepIndex: index,
                  stepDoneLabel: t('wizardNavigationStepDoneAriaLabel', {
                    stepName: `${index + 1}. ${t(item.textKey)}`,
                  }),
                  stepIsAllowedToProceedFrom:
                    currentStep.isAllowedToProceedFrom,
                  currentIndex,
                  navigationItemIsEnabled: item.isEnabled,
                })

              return (
                <WizardNavigationItem
                  status={navigationItemStatus}
                  key={item.path}
                >
                  {allowStepNavigation &&
                  !('aria-disabled' in routerLinkProps) ? (
                    <RouterLink
                      asComponent={Link}
                      to={item.path}
                      {...routerLinkProps}
                    >
                      {index + 1}. {t(item.textKey)}
                    </RouterLink>
                  ) : (
                    <RouterLink {...routerLinkProps}>
                      {index + 1}. {t(item.textKey)}
                    </RouterLink>
                  )}
                </WizardNavigationItem>
              )
            })}
          </WizardNavigation>
        </WizardNavigationContainer>
      </SidebarGridItem>
    )
  }
)

const getNavigationItemStatus = ({
  isGloballyDisabled,
  isOrderCompleteStep,
  stepIndex,
  stepDoneLabel,
  stepIsAllowedToProceedFrom,
  currentIndex,
  navigationItemIsEnabled,
}: {
  isGloballyDisabled: boolean
  isOrderCompleteStep: boolean
  stepIndex: number
  stepDoneLabel: string
  stepIsAllowedToProceedFrom: boolean
  currentIndex: number
  navigationItemIsEnabled: boolean
}): {
  navigationItemStatus: WizardNavigationItemProps['status']
  routerLinkProps:
    | {
        'aria-current': 'step'
        'aria-label'?: 'string'
      }
    | {
        'aria-disabled': true
        role: 'link'
        tabIndex: 0
        'aria-label'?: string
      }
    | {
        'aria-label'?: string
      }
} => {
  const currentRouterLinkProps = {
    'aria-current': 'step' as const,
  }
  const completedRouterLinkProps = {
    'aria-label': stepDoneLabel,
  }
  const disabledRouterLinkProps = {
    'aria-disabled': true as const,
    role: 'link' as const,
    tabIndex: 0 as const,
  }
  const isCurrent = stepIndex === currentIndex
  if (isOrderCompleteStep) {
    return {
      navigationItemStatus: isCurrent ? 'current-completed' : 'completed',
      routerLinkProps: isCurrent
        ? {
            ...disabledRouterLinkProps,
            ...currentRouterLinkProps,
          }
        : {
            ...disabledRouterLinkProps,
            ...completedRouterLinkProps,
          },
    }
  }
  if (isGloballyDisabled) {
    return {
      navigationItemStatus: 'disabled',
      routerLinkProps: disabledRouterLinkProps,
    }
  }
  if (isCurrent && !stepIsAllowedToProceedFrom) {
    return {
      navigationItemStatus: 'current',
      routerLinkProps: currentRouterLinkProps,
    }
  }
  if (isCurrent && stepIsAllowedToProceedFrom) {
    return {
      navigationItemStatus: 'current-completed',
      routerLinkProps: {
        ...currentRouterLinkProps,
        ...completedRouterLinkProps,
      },
    }
  }
  if (stepIndex < currentIndex) {
    return {
      navigationItemStatus: 'completed',
      routerLinkProps: completedRouterLinkProps,
    }
  }
  if (stepIndex > currentIndex && !navigationItemIsEnabled) {
    return {
      navigationItemStatus: 'coming',
      routerLinkProps: disabledRouterLinkProps,
    }
  }
  if (stepIndex > currentIndex && navigationItemIsEnabled) {
    return {
      navigationItemStatus: 'default',
      routerLinkProps: {},
    }
  }

  return {
    navigationItemStatus: 'disabled',
    routerLinkProps: disabledRouterLinkProps,
  }
}

interface ContentProps {
  certificateType: CertificateTypes
  orderFlowName: OrderFlowName
  steps: Step[]
  step: StepRoutes
  lang: LanguageCode
  innerRef: React.RefObject<HTMLHeadingElement>
}

const Content = observer(
  ({
    certificateType,
    orderFlowName,
    steps,
    step,
    lang,
    innerRef,
  }: ContentProps) => {
    const {
      steps: {
        [orderFlowName]: { showStepValidationErrors },
      },
    } = useStore()

    const Component = getComponent({
      steps,
      step,
    }) as StepComponent
    const isGloballyDisabled = getIsGloballyDisabled(step)
    const currentStep = getCurrentStepItem({
      stepValidatorParams: useStepValidationParams(
        certificateType,
        orderFlowName
      ),
      orderFlowName,
      steps,
      step,
      lang,
    })
    const titleId = 'step-title'

    const stepFirstErrorRef = useRef<FirstErrorImperativeHandle>(null)

    return (
      <ContentGridItem>
        <ContentContainer>
          <StepIndex {...{ titleId, steps, step }} ref={innerRef} />
          <ErrorSummary
            {...{
              certificateType,
              orderFlowName,
              steps,
              step,
              lang,
              showStepValidationErrors,
            }}
          />
          <Component
            titleId={titleId}
            certificateType={certificateType}
            orderFlowName={orderFlowName}
            ref={stepFirstErrorRef}
          />
          {currentStep.buttons !== StepButtons.NONE && (
            <Controls
              {...{
                isGloballyDisabled,
                certificateType,
                orderFlowName,
                steps,
                step,
                lang,
                stepFirstErrorRef,
              }}
            />
          )}
        </ContentContainer>
      </ContentGridItem>
    )
  }
)

const StepIndex = React.forwardRef(function StepIndex(
  {
    titleId,
    steps,
    step,
  }: {
    titleId: string
    steps: Step[]
    step: StepRoutes
  },
  ref: React.ForwardedRef<HTMLHeadingElement>
) {
  const stepIndex = getStepIndex({ steps, step })
  const stepCount = getStepCount(steps)
  const dataTestId = `step-${stepIndex + 1}/${stepCount}`
  const isFinalStep = stepIndex + 1 >= stepCount
  return (
    <Text
      tabIndex={-1}
      ref={ref}
      data-test-id={dataTestId}
      aria-describedby={titleId}
    >
      {isFinalStep ? (
        <LocalizedString stringKey={'wizardStepTitleLastStep'} />
      ) : (
        <>
          <LocalizedString stringKey={'wizardStepTitle'} />{' '}
          {`${stepIndex + 1}/${stepCount}`}
        </>
      )}
    </Text>
  )
})

interface ControlsProps {
  certificateType: CertificateTypes
  orderFlowName: OrderFlowName
  steps: Step[]
  step: StepRoutes
  lang: LanguageCode
  isGloballyDisabled: boolean
  stepFirstErrorRef: React.RefObject<FirstErrorImperativeHandle>
}

const Controls = observer(
  ({
    certificateType,
    orderFlowName,
    steps,
    step,
    lang,
    isGloballyDisabled,
    stepFirstErrorRef,
  }: ControlsProps) => {
    const {
      steps: {
        [orderFlowName]: { showStepValidationErrors },
      },
    } = useStore()

    const navigate = useNavigate()
    const currentStep = getCurrentStepItem({
      stepValidatorParams: useStepValidationParams(
        certificateType,
        orderFlowName
      ),
      orderFlowName,
      steps,
      step,
      lang,
    })
    const customNextButton =
      currentStep.buttons === StepButtons.PREV_AND_CUSTOM_NEXT &&
      currentStep.useCustomNextButton
        ? currentStep.useCustomNextButton()
        : undefined

    const hasStepErrors = currentStep.validationErrors.length > 0
    const onNextClick = currentStep.useOnNextClick()
    const hasOnNextClick = onNextClick !== null

    const prevStepPath = getPrevStepPath({
      steps,
      step,
      lang,
    })
    const nextStepPath = getNextStepPath({
      steps,
      step,
      lang,
    })

    const prevButtonOnClick = () => {
      currentStep.onPrevClick({
        onSuccess: () => prevStepPath && navigate(prevStepPath),
      })
    }

    const nextButtonOnClick = (onNextClick: OnNextClick) => {
      const prevShowStepValidationErrors = showStepValidationErrors
      setShowValidationErrors({ orderFlowName, value: hasStepErrors })

      if (!hasStepErrors) {
        onNextClick({
          onSuccess: () => nextStepPath && navigate(nextStepPath),
        })
      } else if (
        currentStep.validationErrors.length === 1 ||
        prevShowStepValidationErrors
      ) {
        stepFirstErrorRef.current?.getFirstErrorField()?.focus()
      }
      // The error summary will be focused on appear with
      // separate logic when there are more than 1 error.
    }

    return (
      <ControlsContainer>
        <ButtonsContainer>
          {currentStep.buttons !== StepButtons.NEXT && (
            <Button
              data-test-id="wizard-previous-link"
              icon={<IconArrowLeft />}
              variant="secondary"
              disabled={isGloballyDisabled}
              onClick={prevButtonOnClick}
            >
              <LocalizedString stringKey="wizardPrevious" />
            </Button>
          )}
          {orderFlowName === OrderFlowName.AETTODISTUS_SIGNER && (
            <OpenRejectAetTodistuspyyntoDialogButton
              isGloballyDisabled={isGloballyDisabled}
              buttonVariant={hasOnNextClick ? 'secondary' : 'default'}
            />
          )}
          {hasOnNextClick && (
            <Button
              data-test-id="wizard-next-link"
              iconRight={<IconArrowRight />}
              disabled={isGloballyDisabled}
              onClick={() => nextButtonOnClick(onNextClick)}
            >
              <LocalizedString
                stringKey={getNextButtonTranslationText(
                  customNextButton ?? currentStep.buttons
                )}
              />
            </Button>
          )}
        </ButtonsContainer>
      </ControlsContainer>
    )
  }
)

interface ErrorSummaryProps {
  lang: LanguageCode
  certificateType: CertificateTypes
  orderFlowName: OrderFlowName
  steps: Step[]
  step: StepRoutes
  showStepValidationErrors: boolean
}

const ErrorSummary: React.FC<ErrorSummaryProps> = observer(
  ({
    certificateType,
    orderFlowName,
    steps,
    step,
    lang,
    showStepValidationErrors,
  }) => {
    const currentStep = getCurrentStepItem({
      stepValidatorParams: useStepValidationParams(
        certificateType,
        orderFlowName
      ),
      orderFlowName,
      steps,
      step,
      lang,
    })
    const hasStepErrors = currentStep.validationErrors.length > 0
    const focusOnAppear = currentStep.validationErrors.length > 1

    const scrollTargetRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (showStepValidationErrors && !hasStepErrors) {
        setShowValidationErrors({ orderFlowName, value: false })
      }
    }, [showStepValidationErrors, hasStepErrors, orderFlowName])

    useEffect(() => {
      if (showStepValidationErrors && focusOnAppear) {
        scrollTargetRef.current?.focus()
      }
      // Omission of focusOnAppear is intentional, we don't want to rerun
      // the effect (refocus) when number of errors goes from 2 to 1 to 2.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showStepValidationErrors])

    return showStepValidationErrors && hasStepErrors ? (
      <InlineAlert
        status="error"
        data-test-id="order-wizard-error-summary"
        labelText={
          <LocalizedString stringKey="orderWizardStepHasValidationErrors" />
        }
        ariaLiveMode="polite"
        ref={scrollTargetRef}
        tabIndex={-1}
      >
        <ValidationErrorList>
          {currentStep.validationErrors.map((error) => (
            <li key={`${error.propertyName}_${error.errorTranslationKey}`}>
              <LocalizedString stringKey={getPropertyTranslationKey(error)} />
            </li>
          ))}
        </ValidationErrorList>
      </InlineAlert>
    ) : null
  }
)

const OpenRejectAetTodistuspyyntoDialogButton = ({
  isGloballyDisabled,
  buttonVariant,
}: {
  isGloballyDisabled: boolean
  buttonVariant?: ButtonProps['variant']
}) => {
  const aetTodistuspyyntoId = useAetTodistuspyyntoId()
  return (
    <Button
      data-test-id="wizard-reject-todistuspyynto-button"
      variant={buttonVariant ?? 'secondary'}
      disabled={isGloballyDisabled}
      onClick={() => queueDialogRejectAetTodistuspyynto(aetTodistuspyyntoId)}
    >
      <LocalizedString stringKey="wizardAetRejectTodistuspyynto" />
    </Button>
  )
}

const useStepValidationParams = (
  certificateType: CertificateTypes,
  orderFlowName: OrderFlowName
): StepValidatorParamsParam['stepValidatorParams'] => {
  const store = useStore()
  const { pathname } = useLocation()
  const {
    common: { loggedInUser },
  } = store

  const getForm = () => {
    if (certificateType === CertificateTypes.AETTODISTUS) {
      if (orderFlowName === OrderFlowName.AETTODISTUS_SIGNER) {
        return store.aetSignerFormFn(
          getAetTodistuspyyntoIdFromPathname(pathname) as number
        )
      } else {
        return store.aetOrdererForm
      }
    } else if (certificateType === CertificateTypes.UAETTODISTUS) {
      return store.uaetForm
    }
    return store.forms[certificateType]
  }

  return {
    certificateType,
    user: loggedInUser.get(),
    form: getForm(),
  }
}

const getNextButtonTranslationText = (stepButton: StepButtons) => {
  switch (stepButton) {
    case StepButtons.PREV_AND_ORDER: {
      return 'wizardGoToPayment'
    }
    case StepButtons.PREV_AND_SEND: {
      return 'wizardSend'
    }
    case StepButtons.PREV_AND_CONFIRM: {
      return 'wizardConfirm'
    }
    case StepButtons.NEXT:
    case StepButtons.PREV_AND_NEXT: {
      return 'wizardNext'
    }
    case StepButtons.NONE:
    case StepButtons.PREV_AND_CUSTOM_NEXT:
    case StepButtons.PREV_AND_DECLINE: {
      throw new Error('Translation not defined')
    }
    default: {
      assertUnreachableCode(stepButton)
    }
  }
}
